
// src/pages/CourseDetailsPage/components/CourseDetails.jsx
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { getCoursePreviewStructure, getPreviewLessonsByCourse } from "../../../api/CoursesApi";
import "../../../static/Courses.css";

import CourseError from "./CourseError";
import VideoModal from "./VideoModal";
import CourseHero from "./CourseHero";
import CourseMainContent from "./CourseMainContent";
import CourseSidebar from "./CourseSidebar";
import PreviewLessonPlayer from "./PreviewLessonPlayer";

import LoadingState from "../../../components/common/LoadingState";
import Footer from "../../../components/common/Footer";

function CourseDetails() {
    const { slug } = useParams();                    // <-- was: { courseId }
    const [course, setCourse] = useState(null);
    const [previewLessons, setPreviewLessons] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [expandedSubsections, setExpandedSubsections] = useState(new Set());
    const [currentVideo, setCurrentVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null);

    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                setLoading(true);

                const [courseResponse, previewResponse] = await Promise.all([
                    getCoursePreviewStructure(slug),      // <-- was courseId
                    getPreviewLessonsByCourse(slug),      // <-- was courseId
                ]);

                if (previewResponse.data.lessons && previewResponse.data.lessons.length > 0) {
                    const firstVideoLesson = previewResponse.data.lessons.find(
                        (l) => l.video_url || l.video_file
                    );
                    setSelectedLesson(firstVideoLesson || previewResponse.data.lessons[0]);
                }

                setCourse(courseResponse.data);
                setPreviewLessons(previewResponse.data);
            } catch (err) {
                setError("Failed to load course. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {                                   // <-- was courseId
            fetchCourseData();
        }
    }, [slug]);                                       // <-- was courseId

    const toggleSection = (sectionId) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        } else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };

    const toggleSubsection = (subsectionId) => {
        const newExpanded = new Set(expandedSubsections);
        if (newExpanded.has(subsectionId)) {
            newExpanded.delete(subsectionId);
        } else {
            newExpanded.add(subsectionId);
        }
        setExpandedSubsections(newExpanded);
    };

    const playPreviewVideo = (lesson) => {
        if (lesson.is_preview && (lesson.video_url || lesson.video_file)) {
            setCurrentVideo(lesson);
            setShowVideoModal(true);
        }
    };

    const closeVideoModal = () => {
        setShowVideoModal(false);
        setCurrentVideo(null);
    };

    const handleLessonSelect = (lesson) => {
        setSelectedLesson(lesson);
    };

    if (loading) {
        return (
            <LoadingState
                variant="page"
                message="Preparing your learning experience..."
            />
        );
    }

    if (error) return <CourseError error={error} />;
    if (!course) return <CourseError type="not-found" />;

    const lessonsList = previewLessons?.lessons || [];

    return (
        <div>
            <div className="enterprise-course">
                <VideoModal
                    show={showVideoModal}
                    currentVideo={currentVideo}
                    onClose={closeVideoModal}
                />

                <CourseHero course={course} skipEnrollmentCheck={true} />

                <div className="course-container">
                    {lessonsList.length > 0 && (
                        <PreviewLessonPlayer
                            lessons={lessonsList}
                            initialLesson={selectedLesson}
                            onLessonChange={handleLessonSelect}
                            autoPlay={true}
                            className="course-preview-player"
                        />
                    )}

                    <div className="course-layout">
                        <CourseMainContent
                            course={course}
                            previewLessons={previewLessons}
                            expandedSections={expandedSections}
                            expandedSubsections={expandedSubsections}
                            onToggleSection={toggleSection}
                            onToggleSubsection={toggleSubsection}
                            onPlayPreviewVideo={playPreviewVideo}
                        />

                        <CourseSidebar
                            course={course}
                            previewLessons={previewLessons}
                        />
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default CourseDetails;